import { Spinner } from '@/ui-kit'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { clearProlificSession, getProlificCompletionUrl, isProlificMode } from '../prolific/session'
import { markProlificComplete } from '@/features/nectar/prolific/api'
import { useNectarTasks } from '../api'
import type { NectarProject } from '../api/models'
import { useTaskHeartbeat } from '../hooks/useTaskHeartbeat'
import { NectarTaskProgress } from './NectarTaskProgress'
import { NectarTasksAllDone } from './NectarTasksAllDone'
import { TASK_TYPE_COMPONENTS } from './task-types'
import { UnsupportedTaskFallback } from './UnsupportedTaskFallback'

type NectarTaskContentProps = {
  project: NectarProject
}

export const NectarTaskContent = ({ project }: NectarTaskContentProps) => {
  const [submittedIds, setSubmittedIds] = useState<Set<string>>(new Set())
  const isActive = project?.enrollment?.status === 'IN_PROGRESS'
  const { data: taskData, isFetching: isFetchingTasks } = useNectarTasks(isActive ? project.id : undefined)
  const tasks = useMemo(() => taskData?.data ?? [], [taskData?.data])

  const onSubmitted = useCallback((taskId: string) => {
    setSubmittedIds((prev) => new Set(prev).add(taskId))
  }, [])

  const pendingTasks = useMemo(
    () => tasks.filter((t) => t.submissionCount === 0 && !submittedIds.has(t.id)),
    [tasks, submittedIds]
  )
  const totalTasks = tasks.length
  const completedCount = totalTasks - pendingTasks.length
  const currentTask = pendingTasks[0]
  const allDone = !isFetchingTasks && tasks.length > 0 && pendingTasks.length === 0

  useEffect(() => {
    if (allDone && isProlificMode()) {
      const completionUrl = getProlificCompletionUrl()
      if (!completionUrl) return
      markProlificComplete()
        .catch(() => {})
        .finally(() => {
          clearProlificSession()
          window.location.href = completionUrl
        })
    }
  }, [allDone])

  const Renderer = currentTask ? (TASK_TYPE_COMPONENTS.get(currentTask.type) ?? null) : null
  const currentTaskIndex = currentTask ? tasks.findIndex((t) => t.id === currentTask.id) : -1

  useTaskHeartbeat({ projectId: project.id, taskId: currentTask?.id, enabled: isActive })

  return isFetchingTasks && !tasks.length ? (
    <div className="flex w-full justify-center py-6">
      <Spinner className="mt-8" />
    </div>
  ) : (
    <>
      {allDone || project.enrollment?.status === 'PENDING_REVIEW' ? (
        <NectarTasksAllDone
          name={project.name}
          rewardConfig={{ type: project.rewardConfig?.type ?? 'points', amount: project.totalReward ?? 0 }}
          projectId={project.id}
        />
      ) : (
        <>
          <NectarTaskProgress completed={completedCount} total={totalTasks} />
          <div className="mx-auto w-full max-w-[600px]">
            {currentTask && Renderer ? (
              <Renderer
                key={currentTask.id}
                taskId={currentTask.id}
                taskIndex={currentTaskIndex}
                projectId={project.id}
                captchaEnabled={project.captchaEnabled}
                taskReportingEnabled={project.taskReportingEnabled}
                onSubmitted={onSubmitted}
              />
            ) : (
              currentTask && <UnsupportedTaskFallback taskType={currentTask.type} />
            )}
          </div>
        </>
      )}
    </>
  )
}
